import { get, post, put } from "./api";
import {
  SuggestTopicsResponse,
  GenerateContentsResponse,
} from "../ai-content/types/AIContentTypes";
import { search } from "./ProjectApi";

export interface AISettings {
  serviceDescription: string;
  customerLocation: string;
  companyIndustry: string;
  topic: string;
  customPrompt: string;
  language: string;
  timezone: string;
  contentVariation: string;
  startTime: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  includeWeekends: boolean;
  postType: string;
  defaultMediaId: string;
  defaultMediaType: string;
  facebook: boolean;
  twitter: boolean;
  instagram: boolean;
  linkedin: boolean;
  threads: boolean;
  /** Unified AI instructions for date-range content generation. */
  contentInstructions?: string;
}

export async function suggestTopics(
  projectId: string,
  service_description: string,
  customer_location: string,
  company_industry: string,
  language: string
): Promise<SuggestTopicsResponse> {
  const { data } = await post({
    path: `/${projectId}/ai/suggest-topics`,
    data: {
      service_description,
      customer_location,
      company_industry,
      language,
    },
  });
  return data;
}

/** AI content generation can take 60s+. Use a long timeout to avoid "Broken pipe" from client disconnect. */
const AI_GENERATE_TIMEOUT_MS = 120_000;

export async function generateContents(
  projectId: string,
  topic: string,
  customPrompt: string | null,
  startDate: string,
  endDate: string,
  totalDays: number,
  includeWeekends: boolean,
  language: string
): Promise<GenerateContentsResponse> {
  const { data } = await post({
    path: `/${projectId}/ai/generate-contents`,
    data: {
      topic,
      startDate,
      endDate,
      totalDays,
      language,
      includeWeekends,
      customPrompt,
    },
    timeoutMs: AI_GENERATE_TIMEOUT_MS,
  });
  return data;
}

export async function generateSocialPosts(
  projectId: string,
  generateContentsParams: any,
  override: boolean
): Promise<GenerateContentsResponse> {
  const { data } = await post({
    path: `/${projectId}/ai/generate-social-posts?override=${override}`,
    data: generateContentsParams,
    timeoutMs: AI_GENERATE_TIMEOUT_MS,
  });
  return data;
}

export async function generateContentInstructions(
  projectId: string,
  service_description: string,
  customer_location: string,
  company_industry: string,
  language: string
): Promise<{ content_instructions?: string }> {
  const { data } = await post({
    path: `/${projectId}/ai/generate-content-instructions`,
    data: {
      service_description,
      customer_location,
      company_industry,
      language,
    },
  });
  return data;
}

/**
 * Generate email body text using AI (for Gmail compose / Chrome extension).
 * Backend should implement POST /workspaces/:projectId/ai/generate-email with body: { prompt, subject?, recipientContext? }
 */
export async function generateEmailBody(
  projectId: string,
  prompt: string,
  options?: { subject?: string; recipientContext?: string }
): Promise<string> {
  const { data } = await post({
    path: `/${projectId}/ai/generate-email`,
    data: { prompt, ...options },
  });
  return typeof data === "string" ? data : (data?.text ?? data?.content ?? "");
}

export async function resetAIAgent(workspaceId: string) {
  const { data } = await put({
    path: `/workspaces/${workspaceId}/ai-settings/reset-ai-agent`,
  });
  return data;
}

export async function saveAIContentSetting(workspaceId: string, settings: AISettings) {
  const { data } = await put({
    path: `/workspaces/${workspaceId}/ai-settings`,
    data: settings,
  });
  return data;
}

export async function scheduleSocialPost(
  workspaceId: string,
  postId: string,
  publishTime: number,
  requireApproval: boolean
) {
  const { data } = await post({
    path: `/${workspaceId}/social-posting/schedule`,
    data: {
      postId,
      publishTime,
      requireApproval,
    },
  });
  return data;
}

export async function getSocialPostStatus(workspaceId: string, postId: string) {
  const { data } = await get({
    path: `/${workspaceId}/social-posting/status/${postId}`,
  });
  return data;
}

export async function getSocialPostEngagement(workspaceId: string, postId: string) {
  const { data } = await get({
    path: `/${workspaceId}/social-posting/engagement/${postId}`,
  });
  return data;
}

export async function cancelSocialPost(workspaceId: string, postId: string) {
  const { data } = await post({
    path: `/${workspaceId}/social-posting/cancel`,
    data: {
      postId,
    },
  });
  return data;
}

export async function retrySocialPost(workspaceId: string, postId: string) {
  const { data } = await post({
    path: `/${workspaceId}/social-posting/retry`,
    data: {
      postId,
    },
  });
  return data;
}

export async function publishNowSocialPost(workspaceId: string, postId: string) {
  const { data } = await post({
    path: `/${workspaceId}/social-posting/publish-now`,
    data: {
      postId,
    },
  });
  return data;
}

export async function scheduleBatch(
  workspaceId: string,
  payload: {
    postIds: string[];
    publishTime: string | number;
    requireApproval: boolean;
  }
) {
  const { data } = await post({
    path: `/${workspaceId}/social-posting/schedule/batch`,
    data: payload,
  });
  return data;
}

export async function unscheduleAllSocialPosts(workspaceId: string) {
  const { data } = await post({
    path: `/${workspaceId}/social-posting/unschedule-all`,
    data: {},
  });
  return data;
}

export interface SearchSocialPostsParams {
  projectId: string;
  page?: number;
  pageSize?: number;
  filters?: any;
  fields?: string[];
  sorts?: any[];
}

export async function searchSocialPosts(params: SearchSocialPostsParams) {
  const { projectId, page = 0, pageSize = 50, filters = {}, fields = [], sorts = [] } = params;

  const data = await search({
    projectId,
    page,
    pageSize,
    filters,
    view: "GRID",
    types: ["SOCIAL_POST"],
    fields,
    sorts,
  });

  return data;
}

export const updatePostField = async (projectId: string, data: any): Promise<any> => {
  const response = await post({
    path: `/${projectId}/social-posting/update-field`,
    data,
  });
  return response.data;
};
